import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import InstallPrompt from "@/components/InstallPrompt";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Workouts from "@/pages/Workouts";
import Nutrition from "@/pages/Nutrition";
import Recovery from "@/pages/Recovery";
import Goals from "@/pages/Goals";
import AiCoach from "@/pages/AiCoach";
import Programs from "@/pages/Programs";
import ExerciseLibrary from "@/pages/ExerciseLibrary";
import Social from "@/pages/Social";
import Profile from "@/pages/Profile";
import PRs from "@/pages/PRs";
import Calendar from "@/pages/Calendar";
import Achievements from "@/pages/Achievements";
import Upgrade from "@/pages/Upgrade";
import Analytics from "@/pages/Analytics";
import FormCheck from "@/pages/FormCheck";
import Discover from "@/pages/Discover";
import Join from "@/pages/Join";
import Messages from "@/pages/Messages";
import Reviews from "@/pages/Reviews";
import CoachPublic from "@/pages/CoachPublic";
import ProGuard from "@/components/ProGuard";
import PublicAssessment from "@/pages/PublicAssessment";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import CoachClients from "@/pages/coach/CoachClients";
import ClientDetail from "@/pages/coach/ClientDetail";
import ClientAssessment from "@/pages/coach/ClientAssessment";
import TestLibrary from "@/pages/coach/TestLibrary";
import CoachExerciseLibrary from "@/pages/coach/CoachExerciseLibrary";
import CoachPrograms from "@/pages/coach/CoachPrograms";
import CoachTeams from "@/pages/coach/CoachTeams";
import CoachTeamDetail from "@/pages/coach/CoachTeamDetail";
import CoachPerformance from "@/pages/coach/CoachPerformance";
import ImportProgram from "@/pages/coach/ImportProgram";
import Teams from "@/pages/Teams";
import Academy from "@/pages/academy/Academy";
import AcademyNew from "@/pages/academy/AcademyNew";
import AcademyJoin from "@/pages/academy/AcademyJoin";
import AcademyPublic from "@/pages/academy/AcademyPublic";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import PostLogin from "@/pages/PostLogin";
import SessionLogger from "@/pages/athlete/SessionLogger";
import AthleteHub from "@/pages/athlete/AthleteHub";
import AthleteTestLibrary from "@/pages/athlete/AthleteTestLibrary";
import SoloHub from "@/pages/solo/SoloHub";
import FitnessTrainerDashboard from "@/pages/trainer/FitnessTrainerDashboard";
import SportsCoachDashboard from "@/pages/sports/SportsCoachDashboard";
import SportsCommandDeck from "@/pages/sports/SportsCommandDeck";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentCommandDeck from "@/pages/parent/ParentCommandDeck";
import { AcademyProvider } from "@/contexts/AcademyContext";
import Layout from "@/components/Layout";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-base">
        <div className="text-text-secondary label-tiny">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
};

const AppRouter = () => {
  const location = useLocation();
  // Synchronous check: if returning from OAuth, handle callback FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/workouts" element={<Protected><Workouts /></Protected>} />
      <Route path="/nutrition" element={<Protected><Nutrition /></Protected>} />
      <Route path="/recovery" element={<Protected><Recovery /></Protected>} />
      <Route path="/goals" element={<Protected><Goals /></Protected>} />
      <Route path="/ai-coach" element={<Protected><AiCoach /></Protected>} />
      <Route path="/programs" element={<Protected><ProGuard slug="unlimited-programs" label="Programs"><Programs /></ProGuard></Protected>} />
      <Route path="/session/:assignment_id/w/:week/d/:day" element={<Protected><SessionLogger /></Protected>} />
      <Route path="/exercises" element={<Protected><ExerciseLibrary /></Protected>} />
      <Route path="/social" element={<Protected><Social /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/prs" element={<Protected><PRs /></Protected>} />
      <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
      <Route path="/achievements" element={<Protected><Achievements /></Protected>} />
      <Route path="/upgrade" element={<Protected><Upgrade /></Protected>} />
      <Route path="/analytics" element={<Protected><ProGuard slug="analytics" label="Deep Analytics"><Analytics /></ProGuard></Protected>} />
      <Route path="/form-check" element={<Protected><ProGuard slug="form-check" label="AI Form Check"><FormCheck /></ProGuard></Protected>} />
      <Route path="/discover" element={<Protected><Discover /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/reviews" element={<Protected><Reviews /></Protected>} />
      <Route path="/join/:code" element={<Join />} />
      <Route path="/share/assessment/:token" element={<PublicAssessment />} />
      <Route path="/c/:slug" element={<CoachPublic />} />
      <Route path="/coach" element={<Protected><CoachDashboard /></Protected>} />
      <Route path="/strength-coach" element={<Protected><CoachDashboard /></Protected>} />
      <Route path="/fitness-trainer" element={<Protected><FitnessTrainerDashboard /></Protected>} />
      <Route path="/sports-coach" element={<Protected><SportsCommandDeck /></Protected>} />
      <Route path="/sports-coach/legacy" element={<Protected><SportsCoachDashboard /></Protected>} />
      <Route path="/parent" element={<Protected><ParentCommandDeck /></Protected>} />
      <Route path="/parent/legacy" element={<Protected><ParentDashboard /></Protected>} />
      <Route path="/athlete" element={<Protected><AthleteHub /></Protected>} />
      <Route path="/athlete/tests" element={<Protected><AthleteTestLibrary /></Protected>} />
      <Route path="/solo" element={<Protected><SoloHub /></Protected>} />
      <Route path="/client" element={<Protected><Dashboard /></Protected>} />
      <Route path="/coach/clients" element={<Protected><CoachClients /></Protected>} />
      <Route path="/coach/clients/:client_id" element={<Protected><ClientDetail /></Protected>} />
      <Route path="/coach/clients/:client_id/assessment" element={<Protected><ClientAssessment /></Protected>} />
      <Route path="/coach/test-library" element={<Protected><TestLibrary /></Protected>} />
      <Route path="/coach/exercises" element={<Protected><CoachExerciseLibrary /></Protected>} />
      <Route path="/coach/programs" element={<Protected><CoachPrograms /></Protected>} />
      <Route path="/coach/import/:code" element={<Protected><ImportProgram /></Protected>} />
      <Route path="/coach/teams" element={<Protected><CoachTeams /></Protected>} />
      <Route path="/coach/teams/:team_id" element={<Protected><CoachTeamDetail /></Protected>} />
      <Route path="/coach/performance" element={<Protected><CoachPerformance /></Protected>} />
      <Route path="/teams" element={<Protected><Teams /></Protected>} />
      <Route path="/academy" element={<Protected><Academy /></Protected>} />
      <Route path="/academy/new" element={<Protected><AcademyNew /></Protected>} />
      <Route path="/academy-join/:code" element={<AcademyJoin />} />
      <Route path="/a/:slug" element={<AcademyPublic />} />
      <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
      <Route path="/post-login" element={<Protected><PostLogin /></Protected>} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-surface-base text-white">
      <BrowserRouter>
        <AuthProvider>
          <AcademyProvider>
            <AppRouter />
            <InstallPrompt />
            <Toaster
              position="bottom-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: "#12141A",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#fff",
                },
              }}
            />
          </AcademyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
